import clsx from 'clsx';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface Coordenadas {
  topo: number;
  esquerda: number;
}

/**
 * Menu suspenso acessível, sem dependência externa.
 *
 * Três decisões que parecem exagero e não são — cada uma corrige um bug real:
 *
 * 1. **Portal para o `body`.** Dentro de um contêiner com `overflow` (a barra
 *    lateral rola), o painel seria CORTADO na borda, por mais alto que fosse o
 *    z-index.
 * 2. **`pointerdown`, não `mousedown`,** para o clique-fora. No iOS o Safari só
 *    sintetiza evento de mouse sobre o que ele julga clicável, então tocar numa
 *    área "morta" não fechava o menu. Ponteiro cobre mouse, toque e caneta.
 * 3. **Scroll e resize REPOSICIONAM, não fecham.** No Android o teclado virtual
 *    dispara `resize`; no iOS o overscroll elástico dispara `scroll` com um
 *    pixel de arraste. Fechando nesses eventos, o menu sumia no mesmo gesto em
 *    que abria. Só fecha quando o gatilho sai mesmo de vista, com folga — no
 *    elástico do iOS o retângulo salta para fora por um frame.
 *
 * `children` aceita render-prop `(fechar) => ...` para fechar ao escolher.
 */
export function Dropdown({
  gatilho,
  rotulo,
  classeGatilho,
  classePainel,
  alinhamento = 'fim',
  children,
}: {
  gatilho: ReactNode;
  rotulo: string;
  classeGatilho?: string;
  classePainel?: string;
  alinhamento?: 'inicio' | 'fim';
  children: ReactNode | ((fechar: () => void) => ReactNode);
}) {
  const [aberto, setAberto] = useState(false);
  const [coords, setCoords] = useState<Coordenadas | null>(null);
  const refGatilho = useRef<HTMLButtonElement>(null);
  const refPainel = useRef<HTMLDivElement>(null);

  const fechar = () => setAberto(false);

  /**
   * Mede gatilho e painel e decide posição ANTES do paint.
   *
   * O painel fica invisível até ser medido; sem isso ele aparece no canto e
   * "pula" para o lugar certo no frame seguinte.
   */
  const medir = useCallback(() => {
    const botao = refGatilho.current;
    if (!botao) return;

    const r = botao.getBoundingClientRect();
    const painel = refPainel.current;
    const alturaPainel = painel?.offsetHeight ?? 0;
    const larguraPainel = painel?.offsetWidth ?? 0;

    // Abre para CIMA quando não cabe embaixo — senão o menu de um gatilho
    // perto do rodapé obriga a rolar a página para ver as opções.
    const abaixo = window.innerHeight - r.bottom;
    const acima = r.top;
    const paraCima = abaixo < alturaPainel + 8 && acima > abaixo;

    const topo = paraCima
      ? Math.max(8, r.top - alturaPainel - 4)
      : Math.min(r.bottom + 4, window.innerHeight - 8);

    let esquerda = alinhamento === 'inicio' ? r.left : r.right - larguraPainel;
    esquerda = Math.max(8, Math.min(esquerda, window.innerWidth - larguraPainel - 8));

    setCoords({ topo, esquerda });
  }, [alinhamento]);

  useLayoutEffect(() => {
    if (aberto) medir();
  }, [aberto, medir]);

  useEffect(() => {
    if (!aberto) return;

    const aoApontar = (e: PointerEvent) => {
      const alvo = e.target;
      if (!(alvo instanceof Node)) return;
      if (refGatilho.current?.contains(alvo)) return;
      if (refPainel.current?.contains(alvo)) return;
      setAberto(false);
    };

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };

    const FOLGA_OVERSCROLL = 48;
    let quadro = 0;
    const acompanhar = (e?: Event) => {
      // `resize` e o scroll da PÁGINA chegam com `target` = Window, que não é
      // Node: `contains(window)` lançaria TypeError justamente no caso mais
      // comum. Só ignora quando o evento nasceu DENTRO do painel.
      const alvo = e?.target;
      if (alvo instanceof Node && refPainel.current?.contains(alvo)) return;
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(() => {
        const r = refGatilho.current?.getBoundingClientRect();
        if (!r) return;
        if (
          r.bottom < -FOLGA_OVERSCROLL ||
          r.top > window.innerHeight + FOLGA_OVERSCROLL
        ) {
          setAberto(false);
          return;
        }
        medir();
      });
    };

    document.addEventListener('pointerdown', aoApontar);
    document.addEventListener('keydown', aoTeclar);
    window.addEventListener('scroll', acompanhar, true);
    window.addEventListener('resize', acompanhar);
    return () => {
      cancelAnimationFrame(quadro);
      document.removeEventListener('pointerdown', aoApontar);
      document.removeEventListener('keydown', aoTeclar);
      window.removeEventListener('scroll', acompanhar, true);
      window.removeEventListener('resize', acompanhar);
    };
  }, [aberto, medir]);

  // Zera a medida ao fechar: a próxima abertura remede do zero, em vez de usar
  // por um frame uma posição de quando o layout era outro.
  useEffect(() => {
    if (!aberto) setCoords(null);
  }, [aberto]);

  return (
    <div className="relative">
      <button
        ref={refGatilho}
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={rotulo}
        onClick={() => setAberto((v) => !v)}
        className={classeGatilho}
      >
        {gatilho}
      </button>

      {aberto &&
        createPortal(
          <div
            ref={refPainel}
            role="menu"
            style={{
              position: 'fixed',
              top: coords?.topo ?? -9999,
              left: coords?.esquerda ?? -9999,
              visibility: coords ? 'visible' : 'hidden',
            }}
            className={clsx(
              'bg-superficie border-tinta-100 animate-entrada z-[60] min-w-52 rounded-xl border p-1 shadow-lg',
              classePainel,
            )}
          >
            {typeof children === 'function' ? children(fechar) : children}
          </div>,
          document.body,
        )}
    </div>
  );
}
